import { getAuth } from 'firebase/auth'

export enum GoogleErrorType {
    BAD_REQUEST = 'BAD_REQUEST', // 400
    UNAUTHORIZED = 'UNAUTHORIZED', // 401
    FORBIDDEN = 'FORBIDDEN', // 403
    NOT_FOUND = 'NOT_FOUND', // 404
    TOO_MANY_REQUESTS = 'TOO_MANY_REQUESTS', // 429
    INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR', // 500
    UNKNOWN = 'UNKNOWN',
}

export class GoogleError extends Error {
    public readonly errorType: GoogleErrorType
    public readonly originalError: any
    public readonly status: number
    public readonly uid: string | undefined

    constructor (message: string, errorType: GoogleErrorType, status: number, originalError: any, uid?: string) {
        super(message)
        this.name = 'GoogleError'
        this.errorType = errorType
        this.status = status
        this.originalError = originalError
        this.uid = uid
    }

    /**
     * Returns a simple string representation for logging.
     * Usage: String(error) or error.toString()
     */
    public toString (): string {
        return `${this.name}: ${this.message} [Status: ${this.status}, Type: ${this.errorType}]`
    }

    /**
     * Custom serialization for JSON.stringify().
     * Ensures message and stack are included.
     */
    public toJSON (): object {
        return {
            name: this.name,
            message: this.message,
            errorType: this.errorType,
            status: this.status,
            uid: this.uid,
            stack: this.stack,
            // Be cautious with originalError if it contains circular references
            // originalError: this.originalError
        }
    }
}

const errorTitles: Record<GoogleErrorType, string> = {
    [GoogleErrorType.BAD_REQUEST]: 'Invalid request',
    [GoogleErrorType.UNAUTHORIZED]: 'Sign-in required',
    [GoogleErrorType.FORBIDDEN]: 'Access denied',
    [GoogleErrorType.NOT_FOUND]: 'Not found',
    [GoogleErrorType.TOO_MANY_REQUESTS]: 'Too many requests',
    [GoogleErrorType.INTERNAL_SERVER_ERROR]: 'Google Drive is unavailable',
    [GoogleErrorType.UNKNOWN]: 'Something went wrong',
}

const errorHints: Record<GoogleErrorType, string> = {
    [GoogleErrorType.BAD_REQUEST]: 'The request sent to Google Drive was invalid. Please try again.',
    [GoogleErrorType.UNAUTHORIZED]: 'Your Google session has expired. Please sign in again.',
    [GoogleErrorType.FORBIDDEN]: 'You don\'t have permission to access this item.',
    [GoogleErrorType.NOT_FOUND]: 'The requested file or folder could not be found. It may have been deleted or moved.',
    [GoogleErrorType.TOO_MANY_REQUESTS]: 'Google Drive rate limit reached. Please wait a moment and try again.',
    [GoogleErrorType.INTERNAL_SERVER_ERROR]: 'Google Drive is having issues right now. Please try again later.',
    [GoogleErrorType.UNKNOWN]: 'An unexpected error occurred while communicating with Google Drive.',
}

/** Short, user-facing title for a GoogleError, based on its error type. */
export function getGoogleErrorTitle (error: GoogleError): string {
    return errorTitles[error.errorType] || errorTitles[GoogleErrorType.UNKNOWN]
}

/** User-facing explanation for a GoogleError, falling back to the raw message. */
export function getGoogleErrorMessage (error: GoogleError): string {
    const hint = errorHints[error.errorType]
    if (!hint) {
        return error.message
    }

    return error.message && error.message !== hint ? `${hint}\n\n${error.message}` : hint
}

export function convertToGoogleError (error: any): GoogleError {
    let status = 0
    let message = 'Unknown Google API Error'
    let errorType = GoogleErrorType.UNKNOWN

    if (error && typeof error === 'object') {
        // Try to extract status
        if (typeof error.status === 'number') {
            status = error.status
        } else if (typeof error.code === 'number') {
            status = error.code
        }

        // Try to extract message
        if (error.result && error.result.error && error.result.error.message) {
            message = error.result.error.message
        } else if (error.response && error.response.data && error.response.data.error && error.response.data.error.message) {
            // axios style or similar
            message = error.response.data.error.message
        } else if (error.message) {
            message = error.message
        } else if (typeof error.body === 'string') {
            try {
                const body = JSON.parse(error.body)
                if (body.error && body.error.message) {
                    message = body.error.message
                }
            } catch {
                // ignore
            }
        }
    }

    // Map status to ErrorType
    switch (status) {
        case 400: {
            errorType = GoogleErrorType.BAD_REQUEST
            break
        }
        case 401: {
            errorType = GoogleErrorType.UNAUTHORIZED
            break
        }
        case 403: {
            errorType = GoogleErrorType.FORBIDDEN
            break
        }
        case 404: {
            errorType = GoogleErrorType.NOT_FOUND
            break
        }
        case 429: {
            errorType = GoogleErrorType.TOO_MANY_REQUESTS
            break
        }
        case 500:
        case 502:
        case 503:
        case 504: {
            errorType = GoogleErrorType.INTERNAL_SERVER_ERROR
            break
        }
        default: {
            if (status >= 500) {
                errorType = GoogleErrorType.INTERNAL_SERVER_ERROR
            } else if (status >= 400 && status < 500) {
                // Fallback for other 4xx errors
                errorType = GoogleErrorType.BAD_REQUEST
            }
            break
        }
    }

    const auth = getAuth()
    const uid = auth.currentUser?.uid

    return new GoogleError(message, errorType, status, error, uid)
}
